import { Plus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  resetConversation,
  type ConversationResponse,
} from '../api/conversation';
import { useT } from '../I18nContext';

/**
 * Sidebar's primary action. Clears the active conversation so the
 * next message starts a fresh thread.
 *
 * No confirmation modal - modern chat apps (ChatGPT, Claude, etc.)
 * don't gate "new conversation" behind a confirm, and PR-D5's
 * History modal will let users restore the previous thread if
 * they wish. Saves a click on the common path.
 *
 * Optimistically empties the conversation cache so the chat
 * surface clears instantly.
 */
export function NewConversationButton() {
  const queryClient = useQueryClient();
  const t = useT();

  const mutation = useMutation({
    mutationFn: resetConversation,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['conversation'] });
      const snapshot = queryClient.getQueryData<ConversationResponse>([
        'conversation',
      ]);
      queryClient.setQueryData<ConversationResponse>(['conversation'], {
        turns: [],
        thread_id: '',
      });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      // Restore on failure - the server presumably rejected, so
      // the conversation is still there.
      if (ctx?.snapshot) {
        queryClient.setQueryData(['conversation'], ctx.snapshot);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation'] });
    },
  });

  return (
    <button
      type="button"
      className="sidebar-primary-btn"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      title={t('new_conversation.button_title')}
    >
      <Plus size={14} />
      <span>{mutation.isPending ? t('new_conversation.resetting') : t('new_conversation.idle')}</span>
    </button>
  );
}
